import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Database, Download, Upload, RefreshCw, Trash2, Code } from 'lucide-react';
import dbService from '../services/dbService';
import toast from 'react-hot-toast';

const DataViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState('flows');
  const [data, setData] = useState<any>({});
  const [jsonView, setJsonView] = useState(false);

  const loadData = () => {
    const dbData = {
      flows: dbService.getAllFlows(),
      contacts: dbService.getContacts(),
      messages: dbService.getMessages({ limit: 50 }),
      groups: dbService.getGroups(),
      analytics: dbService.getAnalytics(),
      logs: dbService.getLogs({ limit: 100 })
    };
    setData(dbData);
  };

  useEffect(() => {
    loadData();
  }, []);

  const tabs = [
    { id: 'flows', label: 'Automazioni', count: data.flows?.length || 0 },
    { id: 'contacts', label: 'Contatti', count: data.contacts?.length || 0 },
    { id: 'messages', label: 'Messaggi', count: data.messages?.length || 0 },
    { id: 'groups', label: 'Gruppi', count: data.groups?.length || 0 },
    { id: 'analytics', label: 'Analytics', count: null },
    { id: 'logs', label: 'Logs', count: data.logs?.length || 0 },
  ];

  const handleReset = () => {
    if (window.confirm('Sei sicuro di voler resettare il database? Questa azione è irreversibile!')) {
      dbService.resetDatabase();
      loadData();
      toast.success('Database resettato con successo!');
    }
  };

  const renderContent = () => {
    const tabData = data[activeTab];
    
    if (jsonView) {
      return (
        <pre className="bg-gray-100 dark:bg-dark-bg p-4 rounded-lg overflow-auto text-xs">
          {JSON.stringify(tabData, null, 2)}
        </pre>
      );
    }

    switch (activeTab) {
      case 'flows':
        return (
          <div className="space-y-4">
            {tabData?.map((flow: any) => (
              <div key={flow.id} className="bg-white dark:bg-dark-surface p-4 rounded-lg shadow">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{flow.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{flow.description}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    flow.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {flow.isActive ? 'Attivo' : 'Inattivo'}
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span>Nodi: {flow.nodes.length}</span> • 
                  <span className="ml-2">Connessioni: {flow.edges.length}</span> •
                  <span className="ml-2">Esecuzioni: {flow.stats.executions}</span> •
                  <span className="ml-2">Success: {flow.stats.successRate}%</span>
                </div>
              </div>
            ))}
          </div>
        );

      case 'contacts':
        return (
          <div className="space-y-3">
            {tabData?.map((contact: any) => (
              <div key={contact.id} className="bg-white dark:bg-dark-surface p-3 rounded-lg shadow flex justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{contact.name}</h4>
                  <p className="text-sm text-gray-500">{contact.phone}</p>
                  <div className="flex gap-2 mt-1">
                    {contact.tags.map((tag: string) => (
                      <span key={tag} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(contact.lastInteraction).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        );

      case 'messages':
        return (
          <div className="space-y-2">
            {tabData?.map((message: any) => (
              <div key={message.id} className="bg-white dark:bg-dark-surface p-3 rounded-lg shadow">
                <div className="flex justify-between items-start">
                  <p className="text-sm text-gray-700 dark:text-gray-300">{message.content}</p>
                  <span className={`text-xs px-2 py-1 rounded ${
                    message.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {message.status}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {new Date(message.timestamp).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        );

      case 'analytics':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Statistiche Giornaliere</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tabData?.daily?.map((day: any) => (
                  <div key={day.date} className="bg-white dark:bg-dark-surface p-4 rounded-lg shadow">
                    <h4 className="font-medium text-gray-700 dark:text-gray-300">{day.date}</h4>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                      <div>Messaggi: {day.messagesProcessed}</div>
                      <div>Esecuzioni: {day.flowExecutions}</div>
                      <div>Nuovi Contatti: {day.newContacts}</div>
                      <div>Errori: {day.errors}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'logs':
        return (
          <div className="space-y-2">
            {tabData?.map((log: any) => (
              <div key={log.id} className="bg-white dark:bg-dark-surface p-3 rounded-lg shadow">
                <div className="flex items-start gap-3">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    log.level === 'error' ? 'bg-red-100 text-red-800' :
                    log.level === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {log.level}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 dark:text-gray-300">{log.message}</p>
                    {log.details && (
                      <pre className="text-xs text-gray-500 mt-1">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8 text-whatsapp-green" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Visualizzatore Database
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Esplora i dati salvati nel sistema
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setJsonView(!jsonView)}
                className={`px-3 py-1 rounded-lg transition-colors ${
                  jsonView 
                    ? 'bg-whatsapp-green text-white' 
                    : 'bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-gray-300'
                }`}
              >
                <Code className="w-4 h-4" />
              </button>
              <button
                onClick={loadData}
                className="p-2 bg-gray-100 dark:bg-dark-bg rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={handleReset}
                className="p-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white shadow-lg'
                  : 'bg-white dark:bg-dark-surface text-gray-700 dark:text-gray-300 hover:shadow-md'
              }`}
            >
              <span>{tab.label}</span>
              {tab.count !== null && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6"
        >
          {renderContent()}
        </motion.div>
      </div>
    </div>
  );
};

export default DataViewer;
